/**
 * Module: `markdown/citations.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import type { SourceMetadata } from "@/types";

/**
 * Citation parser utility to extract source metadata from markdown content
 * Parses structured source sections like "### Source SIDONI :" and extracts
 * citation references with their associated metadata (title, URL, date, summary)
 */

/**
 * Parses source metadata from markdown content
 * Extracts sources from "### Source SIDONI :" and "### Source Légifrance :" sections
 * Returns a Map of citation ID -> SourceMetadata for O(1) lookup
 *
 * Note: Citation IDs (like ekx, n1k) are arbitrary identifiers that may not directly
 * match source titles. This parser extracts all sources and all citation refs,
 * creating entries for citations even if source metadata isn't found.
 */
export function parseCitations(markdown: string): Map<string, SourceMetadata> {
  const sources = new Map<string, SourceMetadata>();
  const sourceList: SourceMetadata[] = [];

  // Match source sections (### Source SIDONI : or ### Source Légifrance :)
  const sourceSectionRegex =
    /###\s+Source\s+([^:]+)\s*:\s*\n([\s\S]*?)(?=\n###\s+Source\s+[^:]+\s*:|$)/g;

  let sectionMatch;
  while ((sectionMatch = sourceSectionRegex.exec(markdown)) !== null) {
    const sourceTypeName = sectionMatch[1].trim();
    const sourceType =
      sourceTypeName === "SIDONI"
        ? "SIDONI"
        : sourceTypeName === "Légifrance"
          ? "Légifrance"
          : "other";
    const sectionContent = sectionMatch[2];

    // Parse individual sources within the section
    // Each source starts with **Titre :** and may have multiple fields
    // Sources are separated by --- or new **Titre :** markers
    const sourceBlocks = sectionContent.split(/\n---\n/);

    sourceBlocks.forEach((block) => {
      const titleMatch = block.match(/\*\*Titre\s*:\*\*\s*([^\n]+)/);
      if (!titleMatch) return;

      const title = titleMatch[1].trim();

      // Extract URL, date, summary from source fields
      const urlMatch = block.match(/\*\*URL\s*:\*\*\s*([^\n]+)/);
      const dateMatch = block.match(
        /\*\*Date\s+de\s+mise\s+à\s+jour\s*:\*\*\s*([^\n]+)/
      );
      const summaryMatch = block.match(/\*\*Résumé\s*:\*\*\s*([^\n]+)/);

      const source: SourceMetadata = {
        id: "", // Will be assigned when we match citations
        title,
        url: urlMatch?.[1]?.trim(),
        date: dateMatch?.[1]?.trim(),
        summary: summaryMatch?.[1]?.trim(),
        sourceType,
      };

      sourceList.push(source);
    });
  }

  // Extract all citation references from the markdown
  // Format: :cite[ekx,n1k] or :cite[ekx]
  const citeRegex = /:cite\[([^\]]+)\]/g;
  const citationRefs = new Set<string>();
  let citeMatch;
  while ((citeMatch = citeRegex.exec(markdown)) !== null) {
    const refs = citeMatch[1].split(",").map((r) => r.trim());
    refs.forEach((ref) => citationRefs.add(ref));
  }

  // Create source entries for all citation refs
  // If we have source metadata, try to match by order (first citation = first source, etc.)
  // Otherwise create placeholder entries
  const citationArray = Array.from(citationRefs);

  citationArray.forEach((ref, index) => {
    // Try to match by index if we have sources
    if (index < sourceList.length && !sourceList[index].id) {
      const source = { ...sourceList[index], id: ref };
      sources.set(ref, source);
    } else {
      // Create placeholder for citation refs without explicit source info
      sources.set(ref, {
        id: ref,
        title: ref.toUpperCase(), // Fallback: show the ref ID
        sourceType: "other",
      });
    }
  });

  // Also add any sources that weren't matched to citations
  sourceList.forEach((source, index) => {
    if (!source.id && index < citationArray.length) {
      const ref = citationArray[index];
      sources.set(ref, { ...source, id: ref });
    }
  });

  return sources;
}

// Re-export SourceMetadata type
export type { SourceMetadata } from "@/types";
